import { Link } from "react-router-dom";
import { BarChart3, ArrowLeft } from "lucide-react";

const UPDATED = "April 20, 2026";

export default function Terms() {
  return (
    <div className="min-h-screen bg-white text-slate-900" style={{ fontFamily: "Manrope" }}>
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="terms-home-link">
            <div className="w-8 h-8 bg-blue-600 rounded text-white flex items-center justify-center">
              <BarChart3 className="w-4 h-4" />
            </div>
            <span className="font-semibold text-slate-900">RSOC × Hub</span>
          </Link>
          <Link to="/login" className="text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1.5">
            <ArrowLeft className="w-3 h-3" /> Back to sign in
          </Link>
        </div>
      </header>

      <article className="max-w-3xl mx-auto px-6 py-12" data-testid="terms-page">
        <h1 className="text-3xl font-semibold tracking-tight mb-2">Terms of Service</h1>
        <p className="text-slate-500 mb-10 text-sm">Last updated: {UPDATED}</p>

        <style>{`
          .legal h2 { font-size: 1.125rem; font-weight: 600; margin-top: 2rem; margin-bottom: 0.5rem; color: #0f172a; }
          .legal h3 { font-size: 0.95rem; font-weight: 600; margin-top: 1.25rem; margin-bottom: 0.4rem; color: #334155; }
          .legal p { color: #475569; line-height: 1.7; margin-bottom: 0.75rem; font-size: 0.9rem; }
          .legal ul { list-style: disc; padding-left: 1.5rem; color: #475569; margin-bottom: 0.75rem; font-size: 0.9rem; line-height: 1.7; }
          .legal ul li { margin-bottom: 0.35rem; }
          .legal a { color: #2563eb; text-decoration: underline; }
          .legal table { width: 100%; border-collapse: collapse; margin: 1rem 0; font-size: 0.85rem; }
          .legal th, .legal td { border: 1px solid #e2e8f0; padding: 8px 10px; text-align: left; vertical-align: top; }
          .legal th { background: #f8fafc; font-weight: 600; color: #334155; }
          .legal strong { color: #0f172a; font-weight: 600; }
        `}</style>

        <div className="legal">        <h2>1. What this is</h2>
        <p>
          RSOC × Hub (the "Service") is a private media-buying and analytics dashboard operated by its customer team
          (the "Operator"). It connects Facebook Ads, System1 RSOC, AI generation providers, and related APIs into one
          workflow. By signing in you ("User") agree to these Terms. If you do not agree, do not use the Service.
        </p>

        <h2>2. Accounts & access</h2>
        <ul>
          <li>Accounts are provisioned by the Operator. You are responsible for keeping your password secret.</li>
          <li>The Operator may suspend access at any time, with or without notice, for policy violations or security reasons.</li>
          <li>You must be at least 18 years old and legally authorized to operate the connected ad accounts.</li>
        </ul>

        <h2>3. Your data & credentials</h2>
        <p>
          To use the Service you connect third-party accounts (Facebook Marketing API, System1, Resend, ElevenLabs, fal.ai,
          OpenAI). You authorize the Operator to store those access tokens and to make API calls on your behalf strictly
          to perform the features you trigger inside the Service (fetch insights, launch/pause campaigns, render creative,
          send transactional emails, forward postbacks). Tokens are stored encrypted at rest and are never sold, shared,
          or used for any purpose outside the Service.
        </p>

        <h2>4. Acceptable use</h2>
        <p>You agree NOT to use the Service to:</p>
        <ul>
          <li>Violate Meta's Advertising Policies, Platform Terms, or System1's partner agreement.</li>
          <li>Run campaigns promoting illegal products, fraud, misleading claims, or prohibited content categories.</li>
          <li>Reverse-engineer, resell, or sublicense the Service.</li>
          <li>Circumvent rate-limits, brute-force endpoints, or attempt unauthorized access.</li>
          <li>Upload content you do not own the rights to, or cause the Service to generate content that infringes third-party IP.</li>
        </ul>

        <h2>5. AI-generated content</h2>
        <p>
          The Service produces text, images, and video via third-party AI providers. You are solely responsible for
          reviewing, editing, and approving AI output before deploying it as an advertisement. The Operator makes no
          warranty that AI output is accurate, policy-compliant, or fit for a particular purpose. You retain ownership
          of the final creative assets you approve.
        </p>

        <h2>6. Ads billed to your Facebook account</h2>
        <p>
          When the Service launches, scales, or promotes a campaign (including Advantage+ Shopping auto-promotion), those
          ads are billed directly to the Facebook Ad Account you connected. The Operator does not underwrite, reimburse,
          or guarantee ad spend. You are responsible for monitoring budgets, circuit breakers, and daily caps.
        </p>

        <h2>7. Availability & SLAs</h2>
        <p>
          The Service is provided "as is" with no uptime guarantee. Scheduled jobs (nightly digests, auto-operator, ASC
          promotion, CAPI postback bridging) are best-effort. Third-party API outages (Facebook, System1, OpenAI, fal.ai,
          ElevenLabs, Resend) may affect functionality and are outside the Operator's control.
        </p>

        <h2>8. Fees</h2>
        <p>
          Hosting, AI generation, and email sending costs are billed directly to the Operator's providers (Emergent,
          Resend, OpenAI, etc.). Facebook ad spend is billed by Meta. If the Operator charges a subscription fee, that
          fee is communicated separately and may be changed with 30 days notice.
        </p>

        <h2>9. Limitation of liability</h2>
        <p>
          To the maximum extent permitted by law, neither the Operator nor its providers are liable for any indirect,
          incidental, special, consequential, or punitive damages, including lost profits, lost ad spend, or lost
          revenue, arising out of your use of the Service. Aggregate liability is capped at the subscription fees
          actually paid to the Operator in the 12 months preceding the claim, or €100, whichever is greater.
        </p>

        <h2>10. Termination</h2>
        <p>
          You may stop using the Service at any time by requesting account deletion at the email listed below. On
          termination, your stored access tokens and personal data are deleted within 30 days (see Privacy Policy).
          Ad campaigns already launched on Meta are not affected and must be managed directly inside Meta Ads Manager.
        </p>

        <h2>11. Changes</h2>
        <p>
          The Operator may update these Terms. Material changes will be posted here with a new "Last updated" date.
          Continued use of the Service after an update constitutes acceptance.
        </p>

        <h2>12. Governing law</h2>
        <p>
          These Terms are governed by the laws of the Operator's jurisdiction of incorporation. Disputes are resolved
          in the competent courts of that jurisdiction.
        </p>

        <h2>13. Contact</h2>
        <p>
          Questions? Contact the Operator at the email address listed in your onboarding invitation. For privacy-specific
          requests, see the <Link to="/privacy" className="text-blue-600 underline">Privacy Policy</Link>.
        </p>

        <hr className="my-12" />
        <p className="text-xs text-slate-500">
          These Terms are boilerplate suitable for an internal team-operated SaaS. Review with qualified legal counsel
          before opening the Service to external paying customers or submitting for Meta App Review.
        </p>
        </div>
      </article>
    </div>
  );
}
